import AHBoolean from "_shared/booleans/boolean";
import { Masked } from "work/masked-fields";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import { ProposalParams } from "work/proposals/_schemas/proposal-schema";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";

/** A field's value reduced to something `===` can compare, or undefined when it has none. */
type ComparableValue = string | number | boolean | undefined;

type UndoableField = {
  field: ProposalField;
  /** The field's value in a spec, or `Masked.MaskedField` when this reader may not see it. */
  read: (spec: ProposalParams) => ComparableValue | typeof Masked.MaskedField;
  /** Writes the saved spec's value back through the field's own builder setter. */
  restore: (builder: ProposalBuilder, saved: ProposalParams) => ProposalBuilder;
};

/**
 * The single-value fields a draft can edit, and how each one is read and put back.
 *
 * The collections (the teams, the fee schedule, the conflicts and policy documents, the reviewers)
 * are not here: a draft edits them a row at a time, and a row removed from a draft is no longer drawn,
 * so there is no place to offer its Undo.
 */
const UNDOABLE_FIELDS: readonly UndoableField[] = [
  {
    field: ProposalField.Client,
    read: (spec) => {
      const clientTeam = spec.workAgreement.clientTeam;
      if (Masked.isMasked(clientTeam)) return Masked.MaskedField;
      return clientTeam?.leader?.userId?.value;
    },
    restore: (builder, saved) => {
      const clientTeam = saved.workAgreement.clientTeam;
      return builder.setClient(Masked.isMasked(clientTeam) ? undefined : clientTeam?.leader);
    },
  },
  {
    field: ProposalField.Name,
    read: (spec) => spec.workAgreement.name?.value || undefined,
    restore: (builder, saved) => builder.setName(saved.workAgreement.name ?? new ProjectName("")),
  },
  {
    field: ProposalField.Description,
    read: (spec) => spec.workAgreement.description?.value || undefined,
    restore: (builder, saved) => builder.setDescription(saved.workAgreement.description ?? new ProjectDescription("")),
  },
  {
    field: ProposalField.Negotiable,
    read: (spec) => spec.negotiable?.value ?? true,
    restore: (builder, saved) => builder.setNegotiable(saved.negotiable?.value ?? true),
  },
  {
    field: ProposalField.ResponseDueBy,
    read: (spec) => spec.responseDueBy?.toISOString(),
    restore: (builder, saved) => builder.setResponseDueBy(saved.responseDueBy),
  },
  {
    field: ProposalField.StartDate,
    read: (spec) => spec.workAgreement.startDate?.toISOString(),
    restore: (builder, saved) => builder.setStartDate(saved.workAgreement.startDate ?? undefined),
  },
  {
    field: ProposalField.EndDate,
    read: (spec) => spec.workAgreement.endDate?.toISOString(),
    restore: (builder, saved) => builder.setEndDate(saved.workAgreement.endDate ?? undefined),
  },
  {
    field: ProposalField.Discount,
    read: (spec) => {
      const discount = spec.workAgreement.discount;
      if (Masked.isMasked(discount)) return Masked.MaskedField;
      return discount?.value ?? 0;
    },
    restore: (builder, saved) => {
      const discount = saved.workAgreement.discount;
      if (Masked.isMasked(discount) || !discount) return builder;
      return builder.setDiscount(discount);
    },
  },
  {
    field: ProposalField.WaiveConflictsCheck,
    // Read as hidden when the documents are hidden too: putting a waiver back can mean clearing them.
    read: (spec) => {
      const waived = spec.workAgreement.conflictsCheckWaived;
      if (Masked.isMasked(waived) || Masked.isMasked(spec.workAgreement.conflicts)) return Masked.MaskedField;
      return waived?.value ?? false;
    },
    // The rule the waiver switch keeps: a waived check carries no conflicts documents, so putting the
    // waiver back removes any that were attached since the save.
    restore: (builder, saved) => {
      const waived = saved.workAgreement.conflictsCheckWaived;
      if (Masked.isMasked(waived)) return builder;
      const restored = builder.setConflictsCheckWaived(new AHBoolean(waived?.value ?? false));
      return waived?.value ? restored.setConflicts([]) : restored;
    },
  },
  {
    field: ProposalField.TeamRestriction,
    read: (spec) => {
      const restricted = spec.workAgreement.teamRestricted;
      if (Masked.isMasked(restricted)) return Masked.MaskedField;
      return restricted?.value ?? false;
    },
    restore: (builder, saved) => {
      const restricted = saved.workAgreement.teamRestricted;
      if (Masked.isMasked(restricted)) return builder;
      return builder.setTeamRestricted(new AHBoolean(restricted?.value ?? false));
    },
  },
];

/**
 * A saved draft's field values: what a draft field's Undo puts back (#6873).
 *
 * The draft page builds one from the proposal as last saved, and only once the draft has been saved.
 * A draft that has never been saved has no saved value to return to, so it has none, and shows no Undo.
 */
export default class SavedDraftFields {
  public constructor(private readonly saved: ProposalParams) {}

  /**
   * Whether `field` holds something other than its saved value in `current`. Always false for a
   * field this reader may not see, and for a field this class does not undo.
   */
  public differ(field: ProposalField, current: ProposalParams): boolean {
    const undoable = findUndoableField(field);
    if (!undoable) return false;

    const savedValue = undoable.read(this.saved);
    const currentValue = undoable.read(current);
    if (Masked.isMasked(savedValue) || Masked.isMasked(currentValue)) return false;
    return savedValue !== currentValue;
  }

  /** Puts `field` back to its saved value, through the same builder setter the field's input uses. */
  public restore(field: ProposalField, builder: ProposalBuilder): ProposalBuilder {
    const undoable = findUndoableField(field);
    if (!undoable) return builder;
    return undoable.restore(builder, this.saved);
  }
}

function findUndoableField(field: ProposalField): UndoableField | undefined {
  return UNDOABLE_FIELDS.find((undoable) => undoable.field.isEqualTo(field));
}
